import React, { useState } from 'react';
import {
  makeStyles,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Collapse,
} from '@material-ui/core';
import { Close, ExpandMore, ExpandLess } from '@material-ui/icons';

const useStyles = makeStyles(theme => ({
  container: {
    backgroundColor: '#f8fafc',
    borderTop: '1px solid #e2e8f0',
    position: 'relative',
  },
  containerSts: {
    backgroundColor: 'transparent !important',
    borderTop: '1px solid #e0e0e0 !important',
  },
  collapsedBar: {
    padding: theme.spacing(1, 2),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: 'pointer',
    backgroundColor: '#f1f5f9',
    borderBottom: '1px solid #e2e8f0',
    '&:hover': { backgroundColor: '#e2e8f0' },
  },
  expandedContent: {
    padding: theme.spacing(1.5, 2),
    backgroundColor: '#f8fafc',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  expandButton: {
    padding: theme.spacing(0.5),
    color: '#64748b',
    '&:hover': { backgroundColor: '#e2e8f0' },
  },
  closeButton: {
    padding: theme.spacing(0.5),
    color: '#64748b',
    '&:hover': { backgroundColor: '#e2e8f0' },
  },
  title: {
    fontSize: '1rem',
    fontWeight: 600,
    color: '#1e293b',
  },
  promptsGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  promptCard: {
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    backgroundColor: '#ffffff',
    border: '1px solid #e2e8f0',
    '&:hover': {
      transform: 'translateY(-1px)',
      backgroundColor: '#fefefe',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
      border: '1px solid #B3D9FF',
    },
  },
  promptContent: {
    padding: `${theme.spacing(1.5)}px !important`,
  },
  promptTitle: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: '#1e293b',
    marginBottom: theme.spacing(0.5),
  },
  promptDescription: {
    fontSize: '0.75rem',
    color: '#64748b',
    lineHeight: 1.4,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  viewMoreButton: {
    fontSize: '0.75rem',
    textTransform: 'none',
    color: '#3b82f6',
    fontWeight: 500,
    padding: theme.spacing(0.5, 1),
    borderRadius: '6px',
    '&:hover': { backgroundColor: 'rgba(59, 130, 246, 0.08)', color: '#2563eb' },
  },
}));

interface PromptContainerProps {
  prompts?: Record<string, any[]>;
  onPromptSelect?: (prompt: string) => void;
  onViewMore?: () => void;
  onClose?: () => void;
  selectedTabValue?: number;
  stsMode?: boolean;
}

export const PromptContainer: React.FC<PromptContainerProps> = ({
  prompts,
  onPromptSelect,
  onViewMore,
  onClose,
  selectedTabValue = 0,
  stsMode = false,
}) => {
  const classes = useStyles();
  const [isExpanded, setIsExpanded] = useState(true);
  const agentPrompts = prompts || {};
  const categories = Object.keys(agentPrompts);

  if (categories.length === 0) return null;

  const getFirstFourPrompts = () => {
    const result = [];
    for (const category of categories) {
      result.push(...(agentPrompts[category] || []));
      if (result.length >= 4) break;
    }
    return result.slice(0, 4);
  };

  const firstPrompts = getFirstFourPrompts();

  return (
    <div className={`${classes.container} ${stsMode ? classes.containerSts : ''}`}>
      <div className={classes.collapsedBar} onClick={() => setIsExpanded(!isExpanded)}>
        <Typography className={classes.title}>Suggested Prompts</Typography>
        <div>
          <IconButton className={classes.expandButton} size="small" onClick={e => { e.stopPropagation(); setIsExpanded(!isExpanded); }}>
            {isExpanded ? <ExpandMore fontSize="small" /> : <ExpandLess fontSize="small" />}
          </IconButton>
          <IconButton className={classes.closeButton} onClick={e => { e.stopPropagation(); onClose?.(); }} size="small">
            <Close fontSize="small" />
          </IconButton>
        </div>
      </div>

      <Collapse in={isExpanded}>
        <div className={classes.expandedContent}>
          <div className={classes.promptsGrid}>
            {firstPrompts.map(prompt => (
              <Card key={prompt.id} className={classes.promptCard} onClick={() => onPromptSelect?.(prompt.description)} elevation={0}>
                <CardContent className={classes.promptContent}>
                  <Typography className={classes.promptTitle}>{prompt.title}</Typography>
                  <Typography className={classes.promptDescription}>{prompt.description}</Typography>
                </CardContent>
              </Card>
            ))}
          </div>
          <Button className={classes.viewMoreButton} onClick={onViewMore} disabled={selectedTabValue === 1}>
            {selectedTabValue === 1 ? 'View more in the prompts tab alongside' : 'View more'}
          </Button>
        </div>
      </Collapse>
    </div>
  );
};
